/**
 * `partDto.ts` — wire shape of a part row for an asset, in
 * `projectstore-metadata`.
 *
 * Mirrors `PartData` (`Models/Data/PartData.cs`) +
 * `DynamoClient.ToPartData` (`shared-application-code/.../DynamoClient.cs:1056`).
 *
 * Dynamo addressing (`DynamoClient.GetParts`,
 * `DynamoClient.cs:1116-1155`):
 *   pk = "{customerId}:asset"
 *   sk between "#asset:{assetId}:part:{MinGuid}"
 *               and "#asset:{assetId}:part:{MaxGuid}"
 *
 * Why parts matter for the GLB-load path: an `EntitySnapshot` does
 * NOT carry an `AssetID` directly. Instead `EntitySnapshotData.PartID`
 * indexes into the parts catalog, and the resolved `PartData` belongs
 * to a specific `AssetID`. Unity's `ProjectDataLoadingContext.
 * GetAssetIDByPartID` is just a `Dictionary<string, string>` populated
 * by walking every loaded asset's parts list — the web port mirrors
 * that with a flat map in `mapEntities.ts`.
 */

import type { AliasDto } from "./aliasData";
import { type NameFloatMap, toNameFloatMap } from "./nameFloatMap";

export interface PartDto {
  /** Mirrors `PartData.ID` — used as the lookup key for snapshots. */
  readonly ID: string;
  readonly Name?: string;
  readonly DisplayName?: string;
  readonly Description?: string;
  /**
   * Slash-separated path describing this part's nesting under the
   * imported asset's mesh hierarchy (`PartData.HierachyID` — note the
   * typo is in Unity verbatim). Used to address the matching
   * Three.js object after GLTF instantiation.
   */
  readonly HierachyID?: string;

  /** Authoring transform; usually unset for root parts. */
  readonly Position?: NameFloatMap | unknown;
  readonly Rotation?: NameFloatMap | unknown;
  readonly Scale?: NameFloatMap | unknown;

  /**
   * Library material assignments for this part (`PartData.MaterialIDs`,
   * Dynamo `L` of `S` — legacy rows may be `SS`, which unmarshalls to a
   * JS `Set`; read through {@link partMaterialIds}). The scene-reset
   * materials leg restores snapshots to this list.
   */
  readonly MaterialIDs?: ReadonlyArray<string> | ReadonlySet<string> | unknown;

  /** Authored visibility (`PartData.Visibility`); absent = visible. */
  readonly Visibility?: boolean;
  /** `PartData.HasMergedChildren` — a merged node owns no renderer of its
   *  own (AI-1173); Unity derives snapshot `HasMergedChildren` from it when
   *  placing a library asset. */
  readonly Merged?: boolean;

  /**
   * LIBRARY part name aliases — Dynamo `L` of `M{Language, Value}`, absent
   * when empty. Web-first field with no Unity counterpart; see
   * `aliasData.ts` for the shape and read rules.
   *
   * This is the reusable copy: Save to Library bakes a snapshot's per-project
   * aliases down onto its part row here, and a later place-from-library seeds
   * fresh snapshots from it. Because the part row IS the library entry, it is
   * also the right-hand side of the out-of-sync comparison — no extra fetch is
   * needed, since these rows are already loaded to resolve the parts catalog.
   */
  readonly Aliases?: ReadonlyArray<AliasDto> | unknown;

  readonly [extra: string]: unknown;
}

/**
 * Normalized `MaterialIDs` read (`AWSDynamoHelpers.SafeGetListOrSetOfStrings`
 * parity: `L` preferred, legacy `SS` tolerated, `[]` default).
 */
export function partMaterialIds(dto: PartDto): ReadonlyArray<string> {
  const raw = dto.MaterialIDs;
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === "string");
  if (raw instanceof Set) {
    return [...raw].filter((v): v is string => typeof v === "string");
  }
  return [];
}

/**
 * Resolve a part's owning `assetId` from the Dynamo `sk`. Used when
 * `GetParts` returns rows that we need to attribute back to assets in
 * a flat list. Sort key shape (per `DynamoClient.cs:1135`):
 *
 *   `#asset:{assetId}:part:{partId}`
 */
export function assetIdFromPartSk(sk: string): string | null {
  const m = /^#asset:([^:]+):part:/.exec(sk);
  return m ? m[1] : null;
}

/**
 * Convenience accessors for mapping consumers — keep the wire shape
 * untouched but expose a typed view for renderer code.
 */
export function partTransform(dto: PartDto): {
  position: NameFloatMap;
  rotation: NameFloatMap;
  scale: NameFloatMap;
} {
  return {
    position: toNameFloatMap(dto.Position),
    rotation: toNameFloatMap(dto.Rotation),
    scale: toNameFloatMap(dto.Scale),
  };
}
